/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {TableModel} from '../../shared/table/models/table.model';
import {
    forkJoin,
    Observable,
    of,
    ReplaySubject,
    Subject,
    Subscription,
    timer
} from 'rxjs';
import {PartNumberChangesApi} from '../../api/ms-partnumberchanges/part-number-changes.api';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {PartNumberChangesMainPageStateModel} from './part-number-changes-main-page-state.model';
import {PartNumberChangesSearchCriteriaService} from '../services/part-number-changes-search-criteria.service';
import {TableLineModel} from '../../shared/table/models/table-line.model';
import {PartNumberChangeSummaryModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-summary.model';
import {TableLineDataModel} from '../../shared/table/models/table-line-data.model';
import {DatepickerHelperService} from '../../shared/utils/datepicker-helper/datepicker-helper.service';
import {PartNumberChangesColumnEnum} from '../models/part-number-changes-column.enum';
import {WithPagination} from '../../shared/pagination/with-pagination';
import {
    finalize,
    take
} from 'rxjs/operators';
import {TableSortedByModel} from '../../shared/table/models/table-sorted-by.model';
import {SortDirection} from '../../api/generic/contracts/sort-direction.enum';
import {TableHeaderEntryModel} from '../../shared/table/models/table-header-entry.model';
import {AbstractFilterStateModel} from '../../shared/filter/abstract-filter-state.model';
import {CodeFilterComponent} from '../../shared/filter/code-filter/code-filter.component';
import {InputAndCodeFilterComponent} from '../../shared/filter/input-and-code-filter/input-and-code-filter.component';
import {DateFilterComponent} from '../../shared/filter/date-filter/date-filter.component';
import {InputFilterComponent} from '../../shared/filter/input-filter/input-filter.component';
import {CodeFilterModel} from '../../shared/filter/code-filter/code-filter.model';
import {ReferenceDataApi} from '../../api/ms-partnumberchanges/reference-data.api';
import {PartnerBaseModel} from '../../api/ms-partnumberchanges/contracts/partner/model/partner-base.model';
import {PartNumberChangeStatusBaseModel} from '../../api/ms-partnumberchanges/contracts/status/model/part-number-change-status-base.model';
import {PartNumberChangeTypeBaseModel} from '../../api/ms-partnumberchanges/contracts/pnc-type/model/part-number-change-type-base.model';
import {PartNumberChangeDetailContract} from '../../api/ms-partnumberchanges/contracts/part-number-change-detail.contract';
import {AppService} from '../../app.service';
import {PartnerGroupBaseModel} from '../../api/ms-partnumberchanges/contracts/partner-group-base.model';
import {Permission} from '../../shared/utils/permission.enum';
import {UserService} from '../../shared/services/user.service';
import {ConfirmModalComponent} from '../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PartNumberChangesFooterActionModel} from '../models/part-number-changes-footer-action.model';
import {PartNumberChangesDetailFooterActionsEnum} from '../models/part-number-changes-detail-footer-actions.enum';
import partNumberChangesModalConfigurations from './part-number-changes-main-page-modals-configuration';
import {PartNumberChangeQueryModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-query.model';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {PartNumberChangeTrialCommandModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-trial-command.model';
import {PartNumberChangeSolveCommandModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-solve-command.model';
import {PartNumberChangesDetailStateModel} from '../part-number-changes-detail/part-number-changes-detail-state.model';
import {StickyHeaderService} from '../../shared/sticky-header/sticky-header.service';
import {Router} from '@angular/router';
import {CodeFilterStateModel} from '../../shared/filter/code-filter/code-filter-state.model';
import {InputAndCodeFilterStateModel} from '../../shared/filter/input-and-code-filter/input-and-code-filter-state.model';
import {TechnicianGroupBaseModel} from '../models/technician-group-model/technician-group-base.model';
import {TechnicianGroupModelMapper} from '../models/technician-group-model/technician-group.model.mapper';
import {DateFilterStateModel} from '../../shared/filter/date-filter/date-filter-state.model';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';
import {SegmentedButtonDisplayModeEnum} from '../../shared/segemented-button/segmented-button-display-mode.enum';
import {PncDetailModel} from '../models/pnc-detail/pnc-detail.model';
import {PncDetailMapper} from '../models/pnc-detail/pnc-detail.mapper';
import {SupplyingPackingPlantBaseMapper} from '../models/supplying-packing-plant/supplying-packing-plant-base.mapper';
import {SupplyingPackingPlantBaseModel} from '../models/supplying-packing-plant/supplying-packing-plant-base.model';
import {PncTabsEnum} from './pnc-tabs.enum';
import {PartNumberChangeResultModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-result.model';
import {PncDoneFilterEnum} from '../models/pnc-done-filter.enum';
import pncMainPageHeadersConfiguration from './pnc-main-page-headers-configuration';
import {PartNumberChangesStickyHeaderActionModel} from '../models/part-number-changes-sticky-header-action.model';
import {FlagConfigurationModel} from '../../shared/flag/flag-configuration.model';
import {UserBaseContract} from '../../api/ms-partnumberchanges/contracts/user-base.contract';
import {SegmentedButtonOptionModel} from '../../shared/segemented-button/segmented-button-option.model';
import {PartNumberChangesCreateNewService} from '../services/part-number-changes-create-new.service';
import {StickyHeaderIconButtonComponent} from '../../shared/sticky-header/html-elements/sticky-header-icon-button.component';
import { HttpErrorResponse } from '@angular/common/http';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {PartNumberChangesExportExcelApi} from '../../api/ms-partnumberchanges/part-number-changes-export-excel.api';
import * as XLSX from 'xlsx';
import moment from 'moment';
import {PrettyPrintMaterialNumberPipe} from '../../shared/pipes/pretty-print-material-number/pretty-print-material-number.pipe';
import {PrettyPrintUpdatedByPipe} from '../../shared/pipes/pretty-print-updated-by/pretty-print-updated-by.pipe';
import {PartNumberChangeReportResultDataMapper} from '../models/mappers/part-number-change-report-result-data.mapper';
import {PartNumberChangeReportResultDetailModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-export-excel/models/part-number-change-report-result-detail.model';
import {PartNumberChangeExportExcelQueryModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-export-excel/models/part-number-change-export-excel-query.model';
import {PartNumberChangeCancelCommandMapper} from '../models/command-cancel/part-number-change-cancel-command.mapper';
import {PartNumberChangeAcceptCommandContract} from '../../api/ms-partnumberchanges/contracts/part-number-change-accept-command.contract';
import {toBase64Utf16LittleEndian} from '../../shared/utils/encode/encodeToBase64Utf16LE';

type WorksheetData = any[][];

@Component({
    selector: 'app-part-number-changes-main-page',
    templateUrl: './part-number-changes-main-page.component.html',
    styleUrls: ['./part-number-changes-main-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesMainPageComponent
        extends StatefulComponent<PartNumberChangesMainPageStateModel>
        implements OnInit, OnDestroy, WithPagination, AfterViewInit {
    @ViewChild('supplyingPackingPlantFilterTemplate', {static: true}) supplyingPackingPlantTemplateRef: TemplateRef<CodeFilterComponent> =
            <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('pncTypeFilterTemplate', {static: true}) pncTypeTemplateRef: TemplateRef<CodeFilterComponent> =
            <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('pncNumberFilterTemplate', {static: true}) pncNumberTemplateRef: TemplateRef<InputAndCodeFilterComponent> =
            <TemplateRef<InputAndCodeFilterComponent>>{};
    @ViewChild('createdByFilterTemplate', {static: true}) createdByTemplateRef: TemplateRef<InputFilterComponent> =
            <TemplateRef<InputFilterComponent>>{};
    @ViewChild('creationDateFilterTemplate', {static: true}) creationDateTemplateRef: TemplateRef<DateFilterComponent> =
            <TemplateRef<DateFilterComponent>>{};
    @ViewChild('partnerFilterTemplate', {static: true}) partnerTemplateRef: TemplateRef<CodeFilterComponent> =
            <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('updatedByFilterTemplate', {static: true}) updatedByTemplateRef: TemplateRef<InputFilterComponent> =
            <TemplateRef<InputFilterComponent>>{};
    @ViewChild('lastModifyFilterTemplate', {static: true}) lastModifyTemplateRef: TemplateRef<DateFilterComponent> =
            <TemplateRef<DateFilterComponent>>{};
    @ViewChild('oldMaterialFilterTemplate', {static: true}) oldMaterialTemplateRef: TemplateRef<InputAndCodeFilterComponent> =
            <TemplateRef<InputAndCodeFilterComponent>>{};
    @ViewChild('statusFilterTemplate', {static: true}) statusTemplateRef: TemplateRef<CodeFilterComponent> =
            <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('newMaterialFilterTemplate', {static: true}) newMaterialTemplateRef: TemplateRef<InputAndCodeFilterComponent> =
            <TemplateRef<InputAndCodeFilterComponent>>{};
    @ViewChild('technicianGroupFilterTemplate', {static: true}) technicianGroupTemplateRef: TemplateRef<CodeFilterComponent> =
            <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('doneFilterTemplate', {static: true}) doneTemplateRef: TemplateRef<CodeFilterComponent> =
            <TemplateRef<CodeFilterComponent>>{};
    @ViewChild('pncTypeFilter') pncTypeComponent?: CodeFilterComponent;
    @ViewChild('pncNumberFilter') pncNumberComponent?: InputAndCodeFilterComponent;
    @ViewChild('creationDateFilter') creationDateComponent?: DateFilterComponent;
    @ViewChild('partnerFilter') partnerComponent?: CodeFilterComponent;
    @ViewChild('updatedByFilter') updatedByComponent?: InputFilterComponent;
    @ViewChild('lastModifyFilter') lastModifyComponent?: DateFilterComponent;
    @ViewChild('oldMaterialFilter') oldMaterialComponent?: InputAndCodeFilterComponent;
    @ViewChild('statusFilter') statusComponent?: CodeFilterComponent;
    @ViewChild('newMaterialFilter') newMaterialComponent?: InputAndCodeFilterComponent;
    @ViewChild('technicianGroupFilter') technicianGroupComponent?: CodeFilterComponent;
    @ViewChild('createdByFilter') createdByComponent?: InputFilterComponent;
    @ViewChild('supplyingPackingPlantFilter') supplyingPackingPlantComponent?: CodeFilterComponent;
    @ViewChild('doneFilter') doneComponent?: CodeFilterComponent;
    public updatePNCDetailState: EventEmitter<Partial<PartNumberChangesDetailStateModel>> =
            new EventEmitter<Partial<PartNumberChangesDetailStateModel>>();
    PartNumberChangesColumnEnum = PartNumberChangesColumnEnum;
    public isToCloseAccordion = new EventEmitter<void>();
    public typeFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public statusFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public partnerFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public technicianGroupFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public supplyingPackingPlantFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public doneFilterCodes: CodeFilterModel[] = [
        {code: PncDoneFilterEnum.DONE, name: 'Done', checked: true, active: true},
        {code: PncDoneFilterEnum.UNDONE, name: 'Undone', checked: true, active: true}
    ];
    // Animation timeout
    private readonly ANIMATION_TIMEOUT = 4800;
    // Event emitter for the scroll events
    public scrollEvent: EventEmitter<void> = new EventEmitter<void>();
    private modalLoadingSubject = new Subject<boolean>();
    private modalEESpecialistSubject = new Subject<boolean>();
    // Subscriptions variable
    private subscriptions: Subscription = new Subscription();
    // Full line tooltip detail
    private readonly FULL_LINE_TOOLTIP_DETAIL: string = 'PNC line';
    // The table data to be injected in the table component
    public tableData: TableModel<PartNumberChangesColumnEnum, PncDetailModel> = {
        headers: {
            entries: new Map<PartNumberChangesColumnEnum, TableHeaderEntryModel<PartNumberChangesColumnEnum>>([
                [PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT, pncMainPageHeadersConfiguration.supplyingPackingPlant],
                [PartNumberChangesColumnEnum.TYPE, pncMainPageHeadersConfiguration.type],
                [PartNumberChangesColumnEnum.NUMBER, pncMainPageHeadersConfiguration.number],
                [PartNumberChangesColumnEnum.CREATED_BY, pncMainPageHeadersConfiguration.createdBy],
                [PartNumberChangesColumnEnum.CREATION_DATETIME, pncMainPageHeadersConfiguration.creationDateTime],
                [PartNumberChangesColumnEnum.PARTNER, pncMainPageHeadersConfiguration.partner],
                [PartNumberChangesColumnEnum.UPDATED_BY, pncMainPageHeadersConfiguration.updatedBy],
                [PartNumberChangesColumnEnum.CHANGED_ON, pncMainPageHeadersConfiguration.changedOn],
                [PartNumberChangesColumnEnum.OLD_MATERIAL_NUMBER, pncMainPageHeadersConfiguration.oldMaterialNumber],
                [PartNumberChangesColumnEnum.STATUS, pncMainPageHeadersConfiguration.status],
                [PartNumberChangesColumnEnum.TECHNICIAN_GROUP, pncMainPageHeadersConfiguration.technicianGroup],
                [PartNumberChangesColumnEnum.NEW_PART_NUMBER, pncMainPageHeadersConfiguration.newPartNumber],
                [PartNumberChangesColumnEnum.DONE, pncMainPageHeadersConfiguration.done]
            ])
        },
        expandableLineTooltipDetail: this.FULL_LINE_TOOLTIP_DETAIL,
        expandableLines: true
    };
    private readonly TABS_INLINE_STYLE: { [key: string]: string } = {'width': '95px'};
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'PNC_MAIN_PAGE_LIST_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    // Excel export
    contextActions: StickyHeaderIconButtonComponent[] = [];
    reportCriteria: PartNumberChangeExportExcelQueryModel;
    private readonly EXCEL_DATE_FORMAT: string = 'DD-MM-YYYY';
    private readonly EXCEL_TIME_FORMAT: string = 'HH:mm:ss';
    private readonly fileName: string = 'orcan-pncs-report.xlsx';
    private readonly worksheetHeader: (string | number)[] = [
        'Supplying packing plant code',
        'Supplying packing plant description',
        'Ship to party code',
        'Ship to party description',
        'PNC Type',
        'PNC number',
        'Created by',
        'Creation date on Date',
        'Creation date on Time',
        'Partner number',
        'Partner description',
        'Updated by',
        'Last modify on Date',
        'Last modify on Time',
        'Old part number',
        'Status',
        'Technicians group (last trial)',
        'New part number (solution)',
        'New part number quantity (solution)',
        'Additional information (solution)'
    ];
    private readonly exportAction: StickyHeaderIconButtonComponent = {
        text: 'Export',
        icon: 'iwp-icon-gen_export_excel',
        click: (): void => {
            this.getPartNumberChangeReport({
                searchCriteria: this.search.getPartNumberChangesQuery().searchCriteria,
                sortCriteria: [
                    {
                        criterion: this.search.sortedBy,
                        sortDirection: this.search.sortDirection
                    }
                ]
            });
        },
        id: 'sticky-header-export-excel-button',
        class: 'btn-text-link',
        dataCy: 'sticky-header-export-excel-button',
        tooltip: 'Export to excel'
    };

    get isLoading(): boolean {
        return this.state?.isLoading ?? false;
    }

    get initiallySortedBy(): TableSortedByModel<PartNumberChangesColumnEnum> {
        let defaultInitialValue = {
            code: PartNumberChangesColumnEnum.CHANGED_ON,
            direction: SortDirection.DESC
        };
        if (this.search != null) {
            defaultInitialValue = {
                code: this.search.sortedBy,
                direction: this.search.sortDirection
            };
        }
        return defaultInitialValue;
    };

    get hasClearAllButton(): boolean {
        return (this.pncTypeComponent?.isFilterActive ?? false)
                || (this.pncNumberComponent?.isFilterActive ?? false)
                || (this.creationDateComponent?.isFilterActive ?? false)
                || (this.partnerComponent?.isFilterActive ?? false)
                || (this.updatedByComponent?.isFilterActive ?? false)
                || (this.lastModifyComponent?.isFilterActive ?? false)
                || (this.oldMaterialComponent?.isFilterActive ?? false)
                || (this.statusComponent?.isFilterActive ?? false)
                || (this.newMaterialComponent?.isFilterActive ?? false)
                || (this.technicianGroupComponent?.isFilterActive ?? false)
                || (this.supplyingPackingPlantComponent?.isFilterActive ?? false)
                || (this.createdByComponent?.isFilterActive ?? false)
                || (this.doneComponent?.isFilterActive ?? false);
    }

    get hasPncTypeColumn(): boolean {
        return !this.state.hasPricePlannerPermission || this.state.hasAdminPermission;
    }

    get hasPartnerColumn(): boolean {
        return !(this.state.hasPartnerPermission || this.state.hasPricePlannerPermission)
                || this.userService.isMultiPartner() || this.state.hasAdminPermission;
    }

    get hasSupplyingPackingPlantColumn(): boolean {
        return !this.state.hasPlannerPermission || this.userService.isMultiSupplyingPackingPlant() || this.state.hasAdminPermission;
    }

    get hasTechnicianGroupColumn(): boolean {
        return !this.state.hasTechnicianPermission || this.userService.isMultiTechnicianGroup() || this.state.hasAdminPermission;
    }

    get hasDoneColumn(): boolean {
        return this.state.selectedTab !== PncTabsEnum.TODO
                && (this.state.hasPartnerPermission || this.state.hasPlannerPermission || this.state.hasAdminPermission);
    }

    /**
     * Returns the correct accordion context that is stored in the component state
     */
    get accordionContext(): PncDetailModel | null {
        let accordionData: PncDetailModel | null = null;
        if (this.state != null && this.state.selectedLine != null) {
            accordionData = this.state.selectedLine.accordionData ?? null;
        }
        return accordionData;
    }

    constructor(public spinnerService: SpinnerService,
                private appService: AppService,
                private dataHelper: DatepickerHelperService,
                public search: PartNumberChangesSearchCriteriaService,
                private referenceDataApi: ReferenceDataApi,
                private partNumberChangesApi: PartNumberChangesApi,
                private userService: UserService,
                private modal: NgbModal,
                private toast: ToastWrapperService,
                private stickyHeaderService: StickyHeaderService,
                private router: Router,
                private partNumberChangesCreateNewService: PartNumberChangesCreateNewService,
                private partNumberChangesExportExcelApi: PartNumberChangesExportExcelApi,
                private prettyPrintMaterialNumberPipe: PrettyPrintMaterialNumberPipe,
                private prettyPrintUpdatedByPipe: PrettyPrintUpdatedByPipe) {
        super();
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesMainPageComponent);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
        this.setInitialState();
        this.updateDynamicColumnsHeaders();

        this.subscriptions.add(this.partNumberChangesCreateNewService.onCreated.subscribe((identifier) => {
            this.storeState({identifier, isAnimated: identifier != null});
        }));
    }

    ngOnInit(): void {
        this.updateTableFiltersComponents();

        if (this.userService.hasPermissions([
            Permission.PARTNER_PNC_CREATE,
            Permission.PLANNER_PNC_CREATE
        ], 'any')) {
            this.stickyHeaderService.registerMainAction({
                text: 'New',
                click: (): void => {
                    this.router.navigate(['/', 'pnc', 'new']);
                },
                id: 'sticky-header-new-button',
                class: 'btn-primary',
                dataCy: 'sticky-header-new-pnc-button',
                tooltip: 'Create new PNC'
            });
        }

        if (this.userService.hasPermissions([
            Permission.ADMIN_PNC_EXPORT,
            Permission.TECHNICIAN_PNC_EXPORT,
            Permission.PARTNER_PNC_EXPORT,
            Permission.PLANNER_PNC_EXPORT,
            Permission.PRICE_PLANNER_PNC_EXPORT
        ], 'any')) {
            this.contextActions.push(this.exportAction);
        }

        this.stickyHeaderService.registerContextActions(this.contextActions);
    }

    ngAfterViewInit(): void {
        this.restoreFilters();
    }

    restoreFilters(): void {
        this.pncTypeComponent?.initFilter(
                this.search?.stateMap.get(PartNumberChangesColumnEnum.TYPE) as InputAndCodeFilterStateModel);
        this.pncNumberComponent?.initFilter(
                this.search?.stateMap.get(PartNumberChangesColumnEnum.NUMBER) as InputAndCodeFilterStateModel);
        this.creationDateComponent?.initFilter(
                this.search?.stateMap.get(PartNumberChangesColumnEnum.CREATION_DATETIME) as DateFilterStateModel);
        this.partnerComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.PARTNER) as CodeFilterStateModel);
        this.createdByComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.CREATED_BY) as InputAndCodeFilterStateModel);
        this.updatedByComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.UPDATED_BY) as InputAndCodeFilterStateModel);
        this.lastModifyComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.CHANGED_ON) as DateFilterStateModel);
        this.oldMaterialComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.OLD_MATERIAL_NUMBER) as InputAndCodeFilterStateModel);
        this.statusComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.STATUS) as CodeFilterStateModel);
        this.technicianGroupComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.TECHNICIAN_GROUP) as InputAndCodeFilterStateModel);
        this.newMaterialComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.NEW_PART_NUMBER) as InputAndCodeFilterStateModel);
        this.supplyingPackingPlantComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT) as CodeFilterStateModel);
        this.doneComponent?.initFilter(
                this.search.stateMap.get(PartNumberChangesColumnEnum.DONE) as CodeFilterStateModel);
    }

    ngOnDestroy(): void {
        this.stickyHeaderService.clearActions();
        this.subscriptions.unsubscribe();
    }

    changePage = (pageNumber: number): void => {
        this.search.pageNumber = pageNumber;

        this.retrieveTableData(true);
    };

    hasPagination(): boolean {
        return this.search.totalResults > this.search.pageSize;
    }

    sortedByChange(sortedBy: TableSortedByModel<PartNumberChangesColumnEnum>): void {
        this.search.sortedBy = sortedBy.code;
        this.search.sortDirection = sortedBy.direction;
        this.retrieveTableData(true);
    }

    filterChange(state: AbstractFilterStateModel): void {
        // To prevent this casting to the column enum, the AbstractFilterStateModel should be generic. Check the interface for more info.
        this.search.setState(state.filterName as PartNumberChangesColumnEnum, state, () => {
            this.retrieveTableData();
        });
    }

    getTypeFilterCodes(): void {
        this.subscriptions.add(this.partNumberChangesApi.getAllPartNumberChangeTypeBase()
                .subscribe((types) => {
                    this.typeFilterSubject.next(this.mapTypeFilterCodes(types));
                }));
    }

    getStatusFilterCodes(): void {
        this.subscriptions.add(this.partNumberChangesApi.getAllPartNumberChangeStatusBase()
                .subscribe((statuses) => {
                    this.statusFilterSubject.next(this.mapStatusFilterCodes(statuses));
                }));
    }

    getSupplyingPackingPlantFilterCodes(): void {
        this.subscriptions.add(this.referenceDataApi.getAllSupplyingPackingPlants()
                .subscribe((plants) => {
                    this.supplyingPackingPlantFilterSubject.next(this.mapSupplyingPackingPlantFilterCodes(
                            SupplyingPackingPlantBaseMapper.fromSupplyingPackingPlantBaseContractArray(plants)));
                }));
    }

    getPartnerFilterCodes(): void {
        this.subscriptions.add(this.referenceDataApi.getAllPartnersGroupedByCountries()
                .subscribe((groups) => {
                    this.partnerFilterSubject.next(this.mapPartnerFilterCodes(groups));
                }));
    }

    getTechnicianGroupFilterCodes(): void {
        this.subscriptions.add(this.referenceDataApi.getAllTechnicianGroups()
                .subscribe((technicianGroups) => {
                    this.technicianGroupFilterSubject.next(this.mapTechnicianGroupFilterCodes(
                            TechnicianGroupModelMapper.fromTechnicianGroupBaseContractArray(technicianGroups)));
                }));
    }

    clearAllFilters(): void {
        this.pncTypeComponent?.resetFilter();
        this.pncNumberComponent?.resetFilter();
        this.creationDateComponent?.resetFilter();
        this.partnerComponent?.resetFilter();
        this.updatedByComponent?.resetFilter();
        this.lastModifyComponent?.resetFilter();
        this.oldMaterialComponent?.resetFilter();
        this.statusComponent?.resetFilter();
        this.newMaterialComponent?.resetFilter();
        this.technicianGroupComponent?.resetFilter();
        this.supplyingPackingPlantComponent?.resetFilter();
        this.createdByComponent?.resetFilter();
        this.doneComponent?.resetFilter();

        this.search.stateMap.clear();

        this.changePage(1);
    }

    /**
     * Retrieves the {@link PartNumberChangeDetailContract} of a specific line {@link TableLineModel} and sets the line.accordionData value
     * @param line the {@link TableLineModel} to which the data must be retrieved
     */
    loadDetailData(line: TableLineModel<PartNumberChangesColumnEnum, PncDetailModel>): void {
        if (line != null && line.accordionData == null) {
            const materialNumber: string | null = line.identifier ?? null;
            if (materialNumber != null) {
                this.subscriptions.add(forkJoin([
                            this.partNumberChangesApi.getPartNumberChange(materialNumber),
                            this.partNumberChangesApi.getPartNumberChangeConfig()
                        ]
                )
                        .subscribe(([detailData, config]) => {
                            line.accordionData = PncDetailMapper.fromPartNumberChangeDetailContract(detailData);
                            line.accordionData.partNumberChangeConfig = config;
                            //  Ensure the line data and configuration is updated
                            const lineSummary = this.getPncSummaryFromDetail(line.accordionData);
                            line = this.mapLineConfiguration(lineSummary, line);

                            this.appService.onRouterOutletContainerScroll
                                    .subscribe(() => {
                                        this.scrollEvent.emit();
                                    });
                        }));
            }
        }
        this.storeState({selectedLine: line});
    }

    markAsDone(pncNumber: string): void {
        this.storeState({tableIsLoading: true});
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.partNumberChangesApi.markPartNumberChangeAsDone(pncNumber)
                        .subscribe(() => {
                            this.toast.success(
                                    `The solved PNC ${pncNumber} was successfully marked as done`,
                                    'Success');
                            this.storeState({isAnimated: true, identifier: pncNumber});
                            this.retrieveTableData(true);
                        })
                        .add(() => {
                            this.storeState({tableIsLoading: false});
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        })
        );
    }

    markAsUndone(pncNumber: string): void {
        this.storeState({tableIsLoading: true});
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.partNumberChangesApi.markPartNumberChangeAsUndone(pncNumber)
                        .subscribe(() => {
                            this.toast.success(
                                    `The solved PNC ${pncNumber} was successfully marked as undone`,
                                    'Success');
                            this.storeState({isAnimated: true, identifier: pncNumber});
                            this.retrieveTableData(true);
                        })
                        .add(() => {
                            this.storeState({tableIsLoading: false});
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        })
        );
    }

    triggerStickyHeaderAction(action: PartNumberChangesStickyHeaderActionModel): void {
        if (action.done) {
            this.markAsDone(action.pncDetail.number);
        } else {
            this.markAsUndone(action.pncDetail.number);
        }
    }

    triggerFooterAction(action: PartNumberChangesFooterActionModel): void {
        switch (action.type) {
            case PartNumberChangesDetailFooterActionsEnum.REJECT:
                this.rejectPNCModal(action.pcnData.number);
                break;
            case PartNumberChangesDetailFooterActionsEnum.DISCARD_TRIAL:
                this.discardTrial();
                break;
            case PartNumberChangesDetailFooterActionsEnum.DISCARD_SOLVE:
                this.discardSolutionAction(action.solutionData);
                break;
            case PartNumberChangesDetailFooterActionsEnum.DISCARD_SEND_SOLUTION:
                this.discardSolutionAction(action.solutionData, true);
                break;
            case PartNumberChangesDetailFooterActionsEnum.SEND_TRIAL:
                this.sendPNC(action.pcnData.number, action.newPncTrial);
                break;
            case PartNumberChangesDetailFooterActionsEnum.SOLVE:
                this.sendPNCSolution(action.pcnData.number, action.solutionData);
                break;
            case PartNumberChangesDetailFooterActionsEnum.ACCEPT || PartNumberChangesDetailFooterActionsEnum.ACCEPT_SOLUTION:
                this.appService.emitShowBackgroundBlur(true);
                this.storeState({tableHasBlur: true, disableAccordion: true});
                break;
            case PartNumberChangesDetailFooterActionsEnum.CANCEL:
                this.cancelPNCModal(action.pcnData.number);
                break;
            case PartNumberChangesDetailFooterActionsEnum.SEND_SOLUTION:
                this.sendToEESpecialistModal(action.pcnData.number, action.solutionData);
                break;
            case PartNumberChangesDetailFooterActionsEnum.REJECT_SOLUTION:
                this.rejectByEESpecialistModal(action.pcnData.number);
                break;
            case PartNumberChangesDetailFooterActionsEnum.SOLVE_AFTER_EE:
                this.sendPNCSolutionEE(action.pcnData.number, action.solutionData.eeSolution);
                break;
            case PartNumberChangesDetailFooterActionsEnum.DISCARD_SOLVE_AFTER_EE:
                this.discardSolutionAction(action.solutionData, false, action.pcnData.requiresFlashing);
                break;
            default:
                return;
        }
    }

    changeListTab(newTab: PncTabsEnum): void {
        if (newTab !== this.state.selectedTab) {
            this.storeState({selectedTab: newTab});
            this.clearAllFilters();
        } else {
            this.retrieveTableData();
        }
    }

    proxyListUpdated(): void {
        this.clearAllFilters();
    }

    mapWorksheetData(reportDetail: PartNumberChangeReportResultDetailModel[]): WorksheetData {
        let worksheetData: WorksheetData = [];
        if (reportDetail != null) {
            worksheetData = reportDetail?.map((reportDetailData: PartNumberChangeReportResultDetailModel) => [
                reportDetailData?.supplyingPackingPlant?.code ? reportDetailData.supplyingPackingPlant.code : null,
                reportDetailData?.supplyingPackingPlant?.name ? reportDetailData.supplyingPackingPlant.name : null,
                reportDetailData?.shipToParty?.code ? reportDetailData.shipToParty.code : null,
                reportDetailData?.shipToParty?.name ? reportDetailData.shipToParty.name : null,
                reportDetailData?.typeName ? reportDetailData.typeName : null,
                reportDetailData?.number ? reportDetailData.number : null,
                reportDetailData?.createdBy ? this.prettyPrintUpdatedByPipe.transform(reportDetailData.createdBy) : null,
                reportDetailData?.creationDatetime ? {
                    v: moment(reportDetailData.creationDatetime)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                reportDetailData?.creationDatetime ? {
                    v: moment(reportDetailData.creationDatetime)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_TIME_FORMAT
                } : null,
                reportDetailData?.partner?.code ? reportDetailData.partner.code : null,
                reportDetailData?.partner?.name ? reportDetailData.partner.name : null,
                reportDetailData?.user ? this.prettyPrintUpdatedByPipe.transform(reportDetailData.user) : null,
                reportDetailData?.lastChangeDatetime ? {
                    v: moment(reportDetailData.lastChangeDatetime)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                reportDetailData?.lastChangeDatetime ? {
                    v: moment(reportDetailData.lastChangeDatetime)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_TIME_FORMAT
                } : null,
                reportDetailData?.material?.number && reportDetailData?.material?.description
                        ? this.prettyPrintMaterialNumberPipe.transform(reportDetailData.material) : null,
                reportDetailData?.statusName ? reportDetailData.statusName : null,
                reportDetailData?.technicianGroupName ? reportDetailData.technicianGroupName : null,
                reportDetailData?.newMaterial?.number && reportDetailData?.newMaterial?.description ?
                        this.prettyPrintMaterialNumberPipe.transform(reportDetailData.newMaterial) : null,
                reportDetailData?.newMaterialQuantity ? reportDetailData.newMaterialQuantity : null,
                reportDetailData?.additionalInformation ? reportDetailData.additionalInformation : null
            ]);
        }
        const worksheet = [this.worksheetHeader].concat(worksheetData);

        return worksheet;
    }

    exportExcel(positions: PartNumberChangeReportResultDetailModel[]): void {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(this.mapWorksheetData(positions), {skipHeader: true});

        // Format the date/time columns, so they don't show up as ######
        worksheet['!cols'] = new Array(this.worksheetHeader.length);
        worksheet['!cols'][5] = {wch: 10.5};
        worksheet['!cols'][6] = {wch: 8.5};

        XLSX.utils.book_append_sheet(workbook, worksheet, 'PNC Report');

        // > The default behavior for all parsers is to generate number cells.
        // > Setting cellDates to true will force the generators to store dates.
        // https://docs.sheetjs.com/#user-content-dates
        XLSX.writeFile(workbook, this.fileName, {cellDates: true});
    }

    private updateTableFiltersComponents(): void {
        if (this.hasPncTypeColumn) {
            this.tableData.headers.entries.get(PartNumberChangesColumnEnum.TYPE).filterType = this.pncTypeTemplateRef;
        }
        if (this.hasSupplyingPackingPlantColumn) {
            this.tableData.headers.entries.get(PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT)
                    .filterType = this.supplyingPackingPlantTemplateRef;
        }
        if (this.hasTechnicianGroupColumn) {
            this.tableData.headers.entries.get(PartNumberChangesColumnEnum.TECHNICIAN_GROUP).filterType = this.technicianGroupTemplateRef;
        }
        if (this.hasPartnerColumn) {
            this.tableData.headers.entries.get(PartNumberChangesColumnEnum.PARTNER).filterType = this.partnerTemplateRef;
        }
        if (this.hasDoneColumn) {
            this.tableData.headers.entries.get(PartNumberChangesColumnEnum.DONE).filterType = this.doneTemplateRef;
        }
        this.tableData.headers.entries.get(PartNumberChangesColumnEnum.NUMBER).filterType = this.pncNumberTemplateRef;
        this.tableData.headers.entries.get(PartNumberChangesColumnEnum.CREATED_BY).filterType = this.createdByTemplateRef;
        this.tableData.headers.entries.get(PartNumberChangesColumnEnum.CREATION_DATETIME).filterType = this.creationDateTemplateRef;
        this.tableData.headers.entries.get(PartNumberChangesColumnEnum.UPDATED_BY).filterType = this.updatedByTemplateRef;
        this.tableData.headers.entries.get(PartNumberChangesColumnEnum.CHANGED_ON).filterType = this.lastModifyTemplateRef;
        this.tableData.headers.entries.get(PartNumberChangesColumnEnum.OLD_MATERIAL_NUMBER).filterType = this.oldMaterialTemplateRef;
        this.tableData.headers.entries.get(PartNumberChangesColumnEnum.STATUS).filterType = this.statusTemplateRef;
        this.tableData.headers.entries.get(PartNumberChangesColumnEnum.NEW_PART_NUMBER).filterType = this.newMaterialTemplateRef;

    }

    private setInitialState(): void {
        this.storeState({
            selectedTab: PncTabsEnum.ALL,
            hasAdminPermission: this.userService.hasPermissions([Permission.ADMIN_PNC_VIEW]),
            hasPartnerPermission: this.userService.hasPermissions([
                Permission.PARTNER_PNC_VIEW,
                Permission.PARTNER_PNC_CREATE,
                Permission.PARTNER_PNC_SEND
            ]),
            hasTechnicianPermission: this.userService.hasPermissions([
                Permission.TECHNICIAN_PNC_VIEW,
                Permission.TECHNICIAN_PNC_SOLVE,
                Permission.TECHNICIAN_PNC_REJECT
            ]),
            hasPlannerPermission: this.userService.hasPermissions([
                Permission.PLANNER_PNC_VIEW,
                Permission.PLANNER_PNC_SEND,
                Permission.PLANNER_PNC_CREATE
            ]),
            hasPricePlannerPermission: this.userService.hasPermissions([
                Permission.PRICE_PLANNER_PNC_VIEW
            ]),
            hasEESpecialistPermission: this.userService.hasPermissions([
                Permission.EE_SPECIALIST_PNC_VIEW,
                Permission.EE_SPECIALIST_PNC_ACCEPT,
                Permission.EE_SPECIALIST_PNC_REJECT
            ])
        });
        // Must be done in a different interaction because the permissions state must already be set
        this.storeState({listTypeOptions: this.getListTabOptions()});
    }

    private setTodoTabCount(todoCount: number): void {
        this.storeState({
            listTypeOptions: this.state.listTypeOptions.map((option) =>
                    option.value === PncTabsEnum.TODO ? {...option, label: `(${todoCount}) TODO`} : option)
        });
    }

    /**
     * Transforms a PncDetailModel in a PartNumberChangeSummaryModel
     * @param lineData updated model with details about the PNC
     * @private
     */
    private getPncSummaryFromDetail(lineData: PncDetailModel): PartNumberChangeSummaryModel {
        return {
            type: lineData.type,
            number: lineData.number,
            creationDatetime: lineData.creationDatetime,
            partner: lineData.partner,
            user: lineData.user,
            lastChangeDatetime: lineData.lastChangeDatetime,
            oldMaterial: lineData.material,
            status: lineData.status,
            newMaterial: lineData.newMaterial?.material,
            technicianGroup: lineData.technicianGroup,
            supplyingPackingPlant: lineData.supplyingPackingPlant,
            createdBy: lineData.createdBy,
            done: lineData.done,
            isTodo: lineData.isTodo
        };
    }

    private mapTypeFilterCodes(types: PartNumberChangeTypeBaseModel[]): CodeFilterModel[] {
        return types?.map((type) => ({
            code: type.code,
            active: type.active,
            name: type.name,
            checked: true
        })) ?? [];
    }

    private mapSupplyingPackingPlantFilterCodes(plants: SupplyingPackingPlantBaseModel[]): CodeFilterModel[] {
        return plants?.map((plant) => ({
            code: plant.code,
            active: plant.active,
            name: plant.name,
            checked: true
        })) ?? [];
    }

    private mapStatusFilterCodes(statuses: PartNumberChangeStatusBaseModel[]): CodeFilterModel[] {
        return statuses?.map((status) => ({
            code: status.code,
            active: status.active,
            name: status.name,
            checked: true
        })) ?? [];
    }

    private mapPartnerFilterCodes(groups: PartnerGroupBaseModel[]): CodeFilterModel[] {
        return groups?.map((partnerGroup) => {
            const codeFilter: CodeFilterModel = {
                name: partnerGroup.country.name,
                code: partnerGroup.country.alpha2Code,
                active: partnerGroup.country.active,
                checked: true,
                indeterminate: false,
                children: []
            };

            partnerGroup.partners?.forEach((partner: PartnerBaseModel) => {
                if (codeFilter.children == null) {
                    codeFilter.children = [];
                }
                codeFilter.children.push({
                    name: partner.name,
                    code: partner.code,
                    active: partner.active,
                    checked: true,
                    indeterminate: false,
                    father: codeFilter
                });
            });

            return codeFilter;
        }) ?? [];
    }

    private mapTechnicianGroupFilterCodes(technicianGroups: TechnicianGroupBaseModel[]): CodeFilterModel[] {
        return technicianGroups?.map((technicianGroup) => ({
            code: technicianGroup.code,
            active: technicianGroup.active,
            name: technicianGroup.name,
            checked: true
        })) ?? [];
    }

    /**
     * Retrieves the table data from the service
     */
    private retrieveTableData(hasPaginationCriteria?: boolean): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        const query: PartNumberChangeQueryModel = this.search.getPartNumberChangesQuery(hasPaginationCriteria);
        const searchService = this.getCurrentTabSearchService();
        const toDoService = (this.state.hasAdminPermission || this.state.hasPricePlannerPermission) ?
                of(0) : this.partNumberChangesApi.getPartNumberChangesToDoCount();
        this.subscriptions.add(
                forkJoin([searchService(query), toDoService])
                        .pipe(finalize(() => {
                            this.storeState({isAnimated: false});
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                            this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesMainPageComponent);
                            this.storeState({isLoading: false});
                        }))
                        .subscribe(([partNumberChanges, todoCount]): void => {
                            this.search.totalResults = partNumberChanges.totalResults;
                            this.tableData.staticSection = this.mapData(partNumberChanges.partNumberChangesPage);

                            const line = this.getLineToAnimate();
                            this.updateLineAnimation(line, this.state.isAnimated);

                            if (this.state.isAnimated) {
                                timer(this.ANIMATION_TIMEOUT)
                                        .subscribe(() => {
                                            this.storeState({isAnimated: false});
                                            this.updateLineAnimation(line, this.state.isAnimated);
                                            this.partNumberChangesCreateNewService.onCreated.next(null);
                                        });
                            }
                            this.setTodoTabCount(todoCount);
                        }));
    }

    private getLineToAnimate(): TableLineModel<PartNumberChangesColumnEnum, PncDetailModel> {
        return this.tableData.staticSection.find((findLine?) =>
                this.state?.identifier === findLine.identifier);
    }

    private updateLineAnimation(line: TableLineModel<PartNumberChangesColumnEnum, PncDetailModel>,
                                isAnimated: boolean): void {
        if (line != null) {
            line.isAnimated = isAnimated;
        }
    }

    /**
     * Populates the staticData array with the current {@link PartNumberChangeSummaryModel}
     * @param partNumberChangeTableData the {@link PartNumberChangeSummaryModel} from which to update the array
     */
    private mapData(partNumberChangeTableData: PartNumberChangeSummaryModel[]):
            TableLineModel<PartNumberChangesColumnEnum, PncDetailModel>[] {
        this.updateDynamicColumnsHeaders();
        this.updateTableFiltersComponents();

        let lines: TableLineModel<PartNumberChangesColumnEnum, PncDetailModel>[] = [];

        if (partNumberChangeTableData != null) {
            lines = partNumberChangeTableData.map((line) => this.mapLineConfiguration(line));
        }

        return lines;
    }

    private mapLineConfiguration = (lineSummary: PartNumberChangeSummaryModel,
                                    line?: TableLineModel<PartNumberChangesColumnEnum, PncDetailModel>):
            TableLineModel<PartNumberChangesColumnEnum, PncDetailModel> => {
        if (line == null) {
            line = {} as TableLineModel<PartNumberChangesColumnEnum, PncDetailModel>;
        }
        line.identifier = lineSummary.number;
        line.data = this.mapLineData(lineSummary);
        line.borderColorIdentification = this.getBorderColorIdentification(lineSummary);
        if (this.isProxiedPnc(lineSummary)) {
            line.hasFlag = true;
            line.flagConfiguration = this.lineFlagConfiguration(lineSummary);
        }
        return line;
    };

    private getBorderColorIdentification(data: PartNumberChangeSummaryModel): string {
        return data?.isTodo ? `pnc-${data?.status?.code ?? ''}` : null;
    }

    /**
     * Maps a {@link PartNumberChangeSummaryModel} to a Map<PartNumberChangesColumnEnum, TableLineDataModel>
     */
    private mapLineData(data: PartNumberChangeSummaryModel): Map<PartNumberChangesColumnEnum, TableLineDataModel> {
        const map: Map<PartNumberChangesColumnEnum, TableLineDataModel> = new Map<PartNumberChangesColumnEnum, TableLineDataModel>([
            [
                PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT, {
                cellValue: data.supplyingPackingPlant
            }
            ],
            [
                PartNumberChangesColumnEnum.TYPE, {
                cellValue: data.type.name
            }
            ],
            [
                PartNumberChangesColumnEnum.NUMBER, {
                cellValue: data.number
            }
            ],
            [
                PartNumberChangesColumnEnum.CREATED_BY, {
                cellValue: data.createdBy
            }
            ],
            [
                PartNumberChangesColumnEnum.CREATION_DATETIME, {
                cellValue: DatepickerHelperService.convertTimestampToDateAndTime(data.creationDatetime)
            }
            ],
            [
                PartNumberChangesColumnEnum.PARTNER, {
                cellValue: data.partner
            }
            ],
            [
                PartNumberChangesColumnEnum.UPDATED_BY, {
                cellValue: data.user
            }
            ],
            [
                PartNumberChangesColumnEnum.CHANGED_ON, {
                cellValue: DatepickerHelperService.convertTimestampToDateAndTime(data.lastChangeDatetime)
            }
            ],
            [
                PartNumberChangesColumnEnum.OLD_MATERIAL_NUMBER, {
                cellValue: data.oldMaterial
            }
            ],
            [
                PartNumberChangesColumnEnum.STATUS, {
                cellValue: data.status,
                dotClass: `pnc-status ${data.status.code}`
            }
            ],
            [
                PartNumberChangesColumnEnum.TECHNICIAN_GROUP, {
                cellValue: data.technicianGroup
            }
            ],
            [
                PartNumberChangesColumnEnum.NEW_PART_NUMBER, {
                cellValue: data.newMaterial
            }
            ],
            [
                PartNumberChangesColumnEnum.DONE, {
                cellValue: data.done
            }
            ]
        ]);
        this.removeHiddenColumnsValues(map);

        return map;
    }

    private updateDynamicColumnsHeaders(): void {
        if (!this.hasPncTypeColumn) {
            this.tableData.headers.entries.delete(PartNumberChangesColumnEnum.TYPE);
        } else if (!this.tableData.headers.entries.has(PartNumberChangesColumnEnum.TYPE)) {
            this.tableData.headers.entries.set(PartNumberChangesColumnEnum.TYPE,
                    pncMainPageHeadersConfiguration.type);
        }

        if (!this.hasPartnerColumn) {
            this.tableData.headers.entries.delete(PartNumberChangesColumnEnum.PARTNER);
        } else if (!this.tableData.headers.entries.has(PartNumberChangesColumnEnum.PARTNER)) {
            this.tableData.headers.entries.set(PartNumberChangesColumnEnum.PARTNER,
                    pncMainPageHeadersConfiguration.partner);
        }

        if (!this.hasSupplyingPackingPlantColumn) {
            this.tableData.headers.entries.delete(PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT);
        } else if (!this.tableData.headers.entries.has(PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT)) {
            this.tableData.headers.entries.set(PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT,
                    pncMainPageHeadersConfiguration.supplyingPackingPlant);
        }

        if (!this.hasTechnicianGroupColumn) {
            this.tableData.headers.entries.delete(PartNumberChangesColumnEnum.TECHNICIAN_GROUP);
        } else if (!this.tableData.headers.entries.has(PartNumberChangesColumnEnum.TECHNICIAN_GROUP)) {
            this.tableData.headers.entries.set(PartNumberChangesColumnEnum.TECHNICIAN_GROUP,
                    pncMainPageHeadersConfiguration.technicianGroup);
        }

        if (!this.hasDoneColumn) {
            this.tableData.headers.entries.delete(PartNumberChangesColumnEnum.DONE);
        } else if (!this.tableData.headers.entries.has(PartNumberChangesColumnEnum.DONE)) {
            this.tableData.headers.entries.set(PartNumberChangesColumnEnum.DONE,
                    pncMainPageHeadersConfiguration.done);
        }
    }

    private removeHiddenColumnsValues(map: Map<PartNumberChangesColumnEnum, TableLineDataModel>): void {
        if (!this.hasPncTypeColumn) {
            map.delete(PartNumberChangesColumnEnum.TYPE);
        }
        if (!this.hasPartnerColumn) {
            map.delete(PartNumberChangesColumnEnum.PARTNER);
        }
        if (!this.hasSupplyingPackingPlantColumn) {
            map.delete(PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT);
        }
        if (!this.hasTechnicianGroupColumn) {
            map.delete(PartNumberChangesColumnEnum.TECHNICIAN_GROUP);
        }
        if (!this.hasDoneColumn) {
            map.delete(PartNumberChangesColumnEnum.DONE);
        }
    }

    /**
     * Call the reject PNC endpoint
     * @param pncNumber the number of the PNC to reject
     * @param motivation the justification por the PNC rejection
     */
    private async rejectPNC(pncNumber: string, motivation: string): Promise<boolean> {
        let success = false;
        await this.partNumberChangesApi.rejectPartNumberChange(pncNumber, {motivationToReject: toBase64Utf16LittleEndian(motivation)})
                .pipe(take(1))
                .toPromise()
                .then(() => {
                    success = true;
                    this.toast.success(`The PNC ${pncNumber} rejection was successfully sent.`, `Success`);
                    this.storeState({isAnimated: true, identifier: pncNumber});
                    this.retrieveTableData(true);
                })
                .finally(() => {
                    this.modalLoadingSubject.next(false);
                });
        return success;
    }

    /**
     * Call the reject PNC endpoint
     * @param pncNumber the number of the PNC to reject
     * @param motivation the justification por the PNC rejection
     */
    private async rejectPNCByEESpecialist(pncNumber: string, motivation: string): Promise<boolean> {
        let success = false;
        await this.partNumberChangesApi.rejectPartNumberChangeSolution(pncNumber,
                {motivationToReject: toBase64Utf16LittleEndian(motivation)})
                .pipe(take(1))
                .toPromise()
                .then(() => {
                    success = true;
                    this.toast.success(`The PNC ${pncNumber} rejection was successfully sent.`, `Success`);
                    this.storeState({isAnimated: true, identifier: pncNumber});
                    this.retrieveTableData(true);
                })
                .finally(() => {
                    this.modalLoadingSubject.next(false);
                });
        return success;
    }

    /**
     * Opens the reject PNC motivation modal
     * @param pncNumber the number of the PNC to reject
     */
    private rejectPNCModal(pncNumber: string): void {
        let isPrimaryAction = false;
        let pncNotes: string;

        const rejectModalConfiguration: ConfirmModalComponent = this.modal.open(ConfirmModalComponent, {
            beforeDismiss: () => {
                if (isPrimaryAction) {
                    isPrimaryAction = false;
                    return this.rejectPNC(pncNumber, pncNotes);
                } else {
                    return true;
                }
            }
        }).componentInstance;

        rejectModalConfiguration.modalConfiguration = {
            ...partNumberChangesModalConfigurations.reject,
            isLoading: this.modalLoadingSubject
        };

        rejectModalConfiguration.primaryEvent.subscribe((notes: string) => {
            this.modalLoadingSubject.next(true);
            isPrimaryAction = true;
            pncNotes = notes;
        });
    }

    /**
     * Call the cancel PNC endpoint
     * @param pncNumber the number of the PNC to cancel
     * @param motivation the justification por the PNC cancelation
     */
    private async cancelPNC(pncNumber: string, motivation: string): Promise<boolean> {
        let success = false;
        await this.partNumberChangesApi.cancelPartNumberChange(pncNumber,
                PartNumberChangeCancelCommandMapper.toPartNumberChangeCancelCommandContract({cancellationReason: motivation}))
                .pipe(take(1))
                .toPromise()
                .then(() => {
                    success = true;
                    this.toast.success(`The PNC ${pncNumber} was successfully cancelled.`, `Success`);
                    this.storeState({isAnimated: true, identifier: pncNumber});
                    this.retrieveTableData(true);
                })
                .finally(() => {
                    this.modalLoadingSubject.next(false);
                });
        return success;
    }

    /**
     * Opens the cancel PNC modal
     * @param pncNumber the number of the PNC to cancel
     */
    private cancelPNCModal(pncNumber: string): void {
        let isPrimaryAction = false;
        let pncNotes: string;

        const cancelationModalConfiguration: ConfirmModalComponent = this.modal.open(ConfirmModalComponent, {
            beforeDismiss: () => {
                if (isPrimaryAction) {
                    isPrimaryAction = false;
                    return this.cancelPNC(pncNumber, pncNotes);
                } else {
                    return true;
                }
            }
        }).componentInstance;

        cancelationModalConfiguration.modalConfiguration = {
            ...partNumberChangesModalConfigurations.cancel,
            isLoading: this.modalLoadingSubject
        };

        cancelationModalConfiguration.primaryEvent.subscribe((notes: string) => {
            this.modalLoadingSubject.next(true);
            isPrimaryAction = true;
            pncNotes = notes;
        });
    }

    /**
     * Call the solve PNC endpoint
     * @param pncNumber the number of the PNC to send
     * @param solution the new PNC solution model
     */
    private sendPNCSolutionEESpecialist(pncNumber: string, solution: PartNumberChangeSolveCommandModel): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.partNumberChangesApi.sendPartNumberChangeSolutionToValidate(pncNumber, solution)
                        .subscribe(() => {
                            this.toast.success(`The PNC ${pncNumber} solution was successfully sent`, `Success`);
                            this.storeState({isAnimated: true, identifier: pncNumber});
                            this.retrieveTableData(true);
                        })
                        .add(() => {
                            this.appService.emitShowBackgroundBlur(false);
                            this.storeState({tableHasBlur: false, disableAccordion: false});
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        }));
    }

    /**
     * Send to EE Specialist PNC modal
     * @param pncNumber the number of the PNC to send
     */
    private sendToEESpecialistModal(pncNumber: string, solution: PartNumberChangeSolveCommandModel): void {
        const sendEESpecialistModalConfiguration: ConfirmModalComponent =
                this.modal.open(ConfirmModalComponent).componentInstance;
        sendEESpecialistModalConfiguration.modalConfiguration = partNumberChangesModalConfigurations.sendEESpecialist;
        sendEESpecialistModalConfiguration.primaryEvent.subscribe(() => this.sendPNCSolutionEESpecialist(pncNumber, solution));
    }

    /**
     * Reject to EE Specialist PNC modal
     * @param pncNumber the number of the PNC to send
     */
    private rejectByEESpecialistModal(pncNumber: string): void {
        let isPrimaryAction = false;
        let pncMotivationToReject: string;

        const rejectModalConfiguration: ConfirmModalComponent = this.modal.open(ConfirmModalComponent, {
            beforeDismiss: () => {
                if (isPrimaryAction) {
                    isPrimaryAction = false;
                    return this.rejectPNCByEESpecialist(pncNumber, pncMotivationToReject);
                } else {
                    return true;
                }
            }
        }).componentInstance;

        rejectModalConfiguration.modalConfiguration = {
            ...partNumberChangesModalConfigurations.rejectSolution,
            isLoading: this.modalLoadingSubject
        };

        rejectModalConfiguration.primaryEvent.subscribe((notes: string) => {
            this.modalLoadingSubject.next(true);
            isPrimaryAction = true;
            pncMotivationToReject = notes;
        });
    }

    /**
     * Call the send PNC endpoint
     * @param pncNumber the number of the PNC to send
     * @param pncNewTrial the new PNC trial
     */
    private sendPNC(pncNumber: string, pncNewTrial: PartNumberChangeTrialCommandModel): void {
        this.storeState({tableIsLoading: true});
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.partNumberChangesApi.sendPartNumberChange(pncNumber, pncNewTrial)
                        .subscribe(() => {
                            this.toast.success(`The PNC ${pncNumber} was successfully sent`, `Success`);
                            this.storeState({isAnimated: true, identifier: pncNumber});
                            this.retrieveTableData(true);
                        })
                        .add(() => {
                            this.storeState({tableIsLoading: false});
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        })
        );
    }

    /**
     * Call the solve PNC endpoint
     * @param pncNumber the number of the PNC to send
     * @param solution the new PNC solution model
     */
    private sendPNCSolution(pncNumber: string, solution: PartNumberChangeSolveCommandModel): void {
        this.storeState({tableIsLoading: true});
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.partNumberChangesApi.solvePartNumberChange(pncNumber, solution)
                        .subscribe(() => {
                            this.storeState({tableHasBlur: false, disableAccordion: false});
                            this.appService.emitShowBackgroundBlur(false);

                            this.toast.success(`The PNC ${pncNumber} solution was successfully sent`, `Success`);
                            this.storeState({isAnimated: true, identifier: pncNumber});
                            this.retrieveTableData(true);
                        })
                        .add(() => {
                            this.storeState({tableIsLoading: false});
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        })
        );
    }

    /**
     * Call the solve PNC endpoint
     * @param pncNumber the number of the PNC to send
     * @param solution the new PNC solution model
     */
    private sendPNCSolutionEE(pncNumber: string, solution: PartNumberChangeAcceptCommandContract): void {
        this.storeState({tableIsLoading: true});
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.partNumberChangesApi.acceptPartNumberChangeSolution(pncNumber, solution)
                        .subscribe(() => {
                            this.storeState({tableHasBlur: false, disableAccordion: false});
                            this.appService.emitShowBackgroundBlur(false);

                            this.toast.success(`The PNC ${pncNumber} solution was successfully sent`, `Success`);
                            this.storeState({isAnimated: true, identifier: pncNumber});
                            this.retrieveTableData(true);
                        })
                        .add(() => {
                            this.storeState({tableIsLoading: false});
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        })
        );
    }

    /**
     * Discard a trial, by close the accordion and loading the PNC list
     */
    private discardTrial(): void {
        this.isToCloseAccordion.emit();
        this.appService.emitShowBackgroundBlur(false);
        this.storeState({selectedLine: null, tableHasBlur: false, disableAccordion: false});
        this.retrieveTableData(true);
    }

    private discardSolutionAction(solutionData: PartNumberChangeSolveCommandModel,
                                  refreshTableData = false,
                                  requiresFlashing?: boolean): void {
        if (this.eeSolutionChanged(solutionData, requiresFlashing) || solutionData != null && !requiresFlashing &&
                (solutionData.additionalMaterials?.length > 0
                        || solutionData.additionalInformation?.length > 0
                        || solutionData.material?.number != null
                )) {
            const discardSolveModalConfiguration: ConfirmModalComponent = this.modal.open(ConfirmModalComponent).componentInstance;

            discardSolveModalConfiguration.modalConfiguration = partNumberChangesModalConfigurations.discardSolve;

            discardSolveModalConfiguration.secondaryEvent.subscribe(() => {
                this.discardSolution(refreshTableData);
            });
        } else {
            this.discardSolution(refreshTableData);
        }
    }

    private eeSolutionChanged(solutionData: PartNumberChangeSolveCommandModel, requiresFlashing: boolean): boolean {
        return requiresFlashing && (solutionData.eeSolution?.eeAdditionalInformation != null
                || solutionData.eeSolution?.iLevel != null);
    }

    private discardSolution(refreshTableData: boolean): void {
        this.updatePNCDetailState.emit({isSolving: false});
        this.appService.emitShowBackgroundBlur(false);
        this.storeState({tableHasBlur: false, disableAccordion: false});
        if (refreshTableData) {
            this.retrieveTableData(true);
        }
    }

    private isProxiedPnc(lineSummary: PartNumberChangeSummaryModel): boolean {
        let isProxied = false;
        if (this.state.hasPlannerPermission && lineSummary.isTodo) {
            isProxied = this.userService.username()
                    ?.toUpperCase() !== lineSummary.createdBy?.username?.toUpperCase();
        }
        return isProxied;
    }

    private lineFlagConfiguration(lineSummary: PartNumberChangeSummaryModel): FlagConfigurationModel {
        return {
            label: this.getUserInitials(lineSummary.createdBy),
            tooltip: this.getFlagTooltip(lineSummary.createdBy),
            backgroundClass: `pnc-status ${lineSummary.status.code}`,
            textColor: '#FFFFFF'
        };
    }

    private getFlagTooltip(user: UserBaseContract): string {
        let tooltip = null;
        if (user != null) {
            if (user.firstName != null && user.lastName != null) {
                tooltip = `${user.firstName} ${user.lastName}`;
            } else {
                tooltip = user.username;
            }
        }
        return tooltip;
    }

    private getUserInitials(user: UserBaseContract): string {
        let initials = null;
        if (user?.firstName != null && user?.lastName != null) {
            initials = `${user.firstName.charAt(0)
                    .toUpperCase()}${user.lastName.charAt(0)
                    .toUpperCase()}`;
        } else if (user?.username != null) {
            initials = `${user.username.substring(0, 2)
                    .toUpperCase()}`;
        }
        return initials;
    }

    private getListTabOptions(): SegmentedButtonOptionModel<PncTabsEnum>[] {
        const allOption: SegmentedButtonOptionModel<PncTabsEnum> = {
            label: 'ALL',
            value: PncTabsEnum.ALL,
            isSelected: true,
            displayMode: SegmentedButtonDisplayModeEnum.BASE,
            inlineStyle: this.TABS_INLINE_STYLE
        };

        const todoOption: SegmentedButtonOptionModel<PncTabsEnum> = {
            label: `TODO`,
            value: PncTabsEnum.TODO,
            isSelected: false,
            displayMode: SegmentedButtonDisplayModeEnum.BASE,
            inlineStyle: this.TABS_INLINE_STYLE
        };

        const myListOption: SegmentedButtonOptionModel<PncTabsEnum> = {
            label: `MY LIST`,
            value: PncTabsEnum.MY_LIST,
            isSelected: false,
            displayMode: SegmentedButtonDisplayModeEnum.BASE,
            inlineStyle: this.TABS_INLINE_STYLE
        };

        let options: SegmentedButtonOptionModel<PncTabsEnum>[] = [];
        if (this.state.hasPartnerPermission || this.state.hasTechnicianPermission || this.state.hasEESpecialistPermission) {
            options = [allOption, todoOption];
        } else if (this.state.hasPlannerPermission) {
            options = [allOption, myListOption, todoOption];
        }
        return options;
    }

    private getCurrentTabSearchService(): (partNumberChangesQuery: PartNumberChangeQueryModel) => Observable<PartNumberChangeResultModel> {
        switch (this.state.selectedTab) {
            case PncTabsEnum.ALL:
                return this.partNumberChangesApi.getPartNumberChanges;
            case PncTabsEnum.TODO:
                return this.partNumberChangesApi.getPartNumberChangesToDo;
            case PncTabsEnum.MY_LIST:
                return this.partNumberChangesApi.getPartNumberChangesMyList;
            default:
                return this.partNumberChangesApi.getPartNumberChanges;
        }
    }

    /**
     * Gets the pnc report response from the BE
     * @param queryModel the search and sort criteria for the query
     * Calls {@link exportExcel}
     * Opens warning modal if exceed-records-number error is returned
     */
    private getPartNumberChangeReport(queryModel: PartNumberChangeExportExcelQueryModel): void {
        this.exportAction.loading = this.exportAction.disabled = true;
        this.stickyHeaderService.registerContextActions(this.contextActions);
        switch (this.state.selectedTab) {
            case PncTabsEnum.ALL:
                this.partNumberChangesExportExcelApi.getPartNumberChangesReport(queryModel)
                        .subscribe((value) => {
                            this.exportExcel(PartNumberChangeReportResultDataMapper
                                    .fromPartNumberChangeReportDetailContractToPartNumberChangeReportResultModelArray(value.reportDetail));
                        }, (error: HttpErrorResponse) => {
                            this.reportErrorModal(error.error.message);
                        })
                        .add(() => {
                            this.actionReportResult();
                        });
                break;
            case PncTabsEnum.TODO:
                this.partNumberChangesExportExcelApi.getPartNumberChangesTodoReport(queryModel)
                        .subscribe((value) => {
                            this.exportExcel(PartNumberChangeReportResultDataMapper
                                    .fromPartNumberChangeReportDetailContractToPartNumberChangeReportResultModelArray(value.reportDetail));
                        }, (error: HttpErrorResponse) => {
                            this.reportErrorModal(error.error.message);
                        })
                        .add(() => {
                            this.actionReportResult();
                        });
                break;
            case PncTabsEnum.MY_LIST:
                this.partNumberChangesExportExcelApi.getPartNumberChangesMyListReport(queryModel)
                        .subscribe((value) => {
                            this.exportExcel(PartNumberChangeReportResultDataMapper
                                    .fromPartNumberChangeReportDetailContractToPartNumberChangeReportResultModelArray(value.reportDetail));
                        }, (error: HttpErrorResponse) => {
                            this.reportErrorModal(error.error.message);
                        })
                        .add(() => {
                            this.actionReportResult();
                        });
                break;
        }
    }

    private reportErrorModal(errorMessage: string): void {
        if (errorMessage != null) {
            const confirmModalInstance: ConfirmModalComponent = this.modal.open(ConfirmModalComponent).componentInstance;
            confirmModalInstance.modalConfiguration = {
                title: 'Warning',
                alertNotificationSeverity: AlertNotificationSeverity.WARNING,
                body: {
                    title: errorMessage
                },
                secondaryActionTitle: 'Close'
            };
        }
    }

    private actionReportResult(): void {
        this.exportAction.loading = this.exportAction.disabled = false;
        this.stickyHeaderService.registerContextActions(this.contextActions);
    }
}
